import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { UpdoxDirectMessage } from '../updox-direct-message';
import { UpdoxMessagesService } from '../updox-messages.service';
import { MessageAttachment, PendingAttachment, DocumentType } from '../updox-attachment';
import { AlertService } from '../../../../_alert';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';

const MAX_POLL_ATTEMPTS = 12; // 12 × 4 s = 48 s maximum wait

@Component({
  selector: 'app-updox-attachments',
  templateUrl: './attachments.component.html',
})
export class UpdoxAttachmentsComponent implements OnChanges, OnDestroy {
  @Input() message: UpdoxDirectMessage;

  attachments: MessageAttachment[] = [];
  pending: PendingAttachment[] = [];
  hasCcda = false;
  isLoading = false;
  pollTimedOut = false;

  // ── Add-to-chart form ──────────────────────────────────────────────────────
  activeAddToChartId: number = null;
  isAddingToChart = false;
  addToChartPatientId: number = null;
  addToChartDocTypeId: number = null;
  addToChartName = '';
  addToChartDate = '';
  documentTypes: DocumentType[] = [];
  documentTypesLoaded = false;

  // Patient typeahead
  patientQuery = '';
  patientResults: any[] = [];
  patientSearchLoading = false;
  selectedPatient: any = null;
  patientAutoDetected = false;
  showPatientDropdown = false;
  private patientSearchTimer: any;
  private patientSearchVersion = 0;

  // ── C-CDA preview ──────────────────────────────────────────────────────────
  activeCcdaAttachment: MessageAttachment = null;
  activeCcdaPendingIndex: number = null;

  private pollTimer: any;
  private pollCount = 0;

  constructor(
    private messagesService: UpdoxMessagesService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.message && this.message?.id) {
      this.reset();
      this.loadAttachments(true);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    clearTimeout(this.patientSearchTimer);
  }

  private reset(): void {
    this.stopPolling();
    clearTimeout(this.patientSearchTimer);
    this.attachments = [];
    this.pending = [];
    this.hasCcda = false;
    this.pollCount = 0;
    this.pollTimedOut = false;
    this.activeAddToChartId = null;
    this.isAddingToChart = false;
    this.activeCcdaAttachment = null;
    this.activeCcdaPendingIndex = null;
    this.resetPatientSearch();
  }

  private resetPatientSearch(): void {
    this.patientQuery = '';
    this.patientResults = [];
    this.patientSearchLoading = false;
    this.selectedPatient = null;
    this.patientAutoDetected = false;
    this.showPatientDropdown = false;
    this.addToChartPatientId = null;
  }

  // ── Polling ────────────────────────────────────────────────────────────────

  loadAttachments(initialLoad = false): void {
    if (!this.message?.id) { return; }
    if (initialLoad) { this.isLoading = true; }

    this.messagesService.getMessageAttachments(this.message.id)
      .then(resp => {
        this.isLoading   = false;
        this.attachments = resp.attachments || [];
        this.pending     = resp.pending     || [];
        this.hasCcda     = resp.has_ccda    || false;

        if (this.pending.length > 0) {
          this.pollCount++;
          if (this.pollCount < MAX_POLL_ATTEMPTS) {
            this.pollTimer = setTimeout(() => this.loadAttachments(false), 4000);
          } else {
            this.pollTimedOut = true;
          }
        } else {
          this.pollCount    = 0;
          this.pollTimedOut = false;
        }
      })
      .catch(() => { this.isLoading = false; });
  }

  retryPolling(): void {
    this.pollCount    = 0;
    this.pollTimedOut = false;
    this.loadAttachments(true);
  }

  private stopPolling(): void {
    if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
  }

  get totalCount(): number {
    return this.attachments.length + this.pending.length;
  }

  // ── Document viewer ────────────────────────────────────────────────────────

  viewDocument(attachment: MessageAttachment): void {
    if (attachment.document_url) { window.open(attachment.document_url, '_blank'); }
  }

  fileIcon(contentType: string): string {
    if (!contentType) { return 'fa-file-o'; }
    if (contentType.includes('pdf'))                                        { return 'fa-file-pdf-o text-danger'; }
    if (contentType.includes('xml') || contentType.includes('text/xml'))   { return 'fa-file-code-o text-info'; }
    if (contentType.includes('image'))                                      { return 'fa-file-image-o text-warning'; }
    return 'fa-file-o text-muted';
  }

  // ── C-CDA preview ──────────────────────────────────────────────────────────

  openCcdaPreview(attachment: MessageAttachment): void {
    this.activeCcdaAttachment   = attachment;
    this.activeCcdaPendingIndex = null;
    this.activeAddToChartId     = null;
  }

  openCcdaPreviewForPending(p: PendingAttachment): void {
    this.activeCcdaPendingIndex = p.index;
    this.activeCcdaAttachment   = null;
    this.activeAddToChartId     = null;
  }

  get ccdaPreviewOpen(): boolean {
    return this.activeCcdaAttachment != null || this.activeCcdaPendingIndex != null;
  }

  closeCcdaPreview(): void {
    this.activeCcdaAttachment   = null;
    this.activeCcdaPendingIndex = null;
  }

  onChartAdded(): void {
    this.closeCcdaPreview();
    this.message.added_to_medical_chart = true;
    this.alertService.success('Document added to patient chart.', this.alertService.options);
  }

  // ── Add-to-chart form ──────────────────────────────────────────────────────

  openAddToChart(attachment: MessageAttachment): void {
    this.activeAddToChartId     = attachment.id;
    this.activeCcdaAttachment   = null;
    this.activeCcdaPendingIndex = null;
    this.addToChartName         = this.message.subject || '';
    this.addToChartDate         = new Date().toISOString().substring(0, 10);
    this.addToChartDocTypeId    = null;
    this.resetPatientSearch();

    // If the message already knows which patient it belongs to, load that patient
    if (this.message.patient_id) {
      this.addToChartPatientId = this.message.patient_id;
      this.patientSearchLoading = true;
      this.messagesService.getPatientById(this.message.patient_id, this.message.practice_id)
        .then(patient => {
          this.patientSearchLoading = false;
          if (patient) {
            this.selectedPatient    = patient;
            this.patientQuery       = patient.name || '';
            this.patientAutoDetected = true;
          } else {
            this.selectedPatient     = { id: this.message.patient_id, name: `Patient #${this.message.patient_id}` };
            this.patientQuery        = this.selectedPatient.name;
            this.patientAutoDetected = true;
          }
        })
        .catch(() => {
          this.patientSearchLoading = false;
          this.selectedPatient     = { id: this.message.patient_id, name: `Patient #${this.message.patient_id}` };
          this.patientQuery        = this.selectedPatient.name;
          this.patientAutoDetected = true;
        });
    }

    if (!this.documentTypesLoaded) {
      this.messagesService.getDocumentTypes()
        .then(types => { this.documentTypes = types || []; this.documentTypesLoaded = true; })
        .catch(() => {});
    }
  }

  closeAddToChart(): void {
    this.activeAddToChartId = null;
    this.resetPatientSearch();
  }

  submitAddToChart(): void {
    if (!this.addToChartPatientId) {
      this.globalErrorHandler.error = 'Please search and select a patient before saving.';
      return;
    }
    this.isAddingToChart = true;
    this.messagesService.addToChart({
      id:               this.message.id,
      attachment_id:    this.activeAddToChartId,
      patient_id:       this.addToChartPatientId,
      document_type_id: this.addToChartDocTypeId  || undefined,
      name:             this.addToChartName.trim() || undefined,
      date:             this.addToChartDate        || undefined,
    }).then(() => {
      this.isAddingToChart              = false;
      this.activeAddToChartId           = null;
      this.message.added_to_medical_chart = true;
      this.alertService.success('Document added to patient chart.', this.alertService.options);
    }).catch(resp => {
      this.isAddingToChart = false;
      this.globalErrorHandler.error = resp?.error?.error || 'Failed to add document to chart.';
    });
  }

  // ── Patient typeahead ──────────────────────────────────────────────────────

  onPatientQueryChange(value: string): void {
    this.selectedPatient     = null;
    this.addToChartPatientId = null;
    clearTimeout(this.patientSearchTimer);
    this.patientResults      = [];
    this.showPatientDropdown = false;

    if (!value || value.trim().length < 2) {
      this.patientSearchLoading = false;
      return;
    }
    this.patientSearchLoading = true;
    this.patientSearchTimer = setTimeout(() => this.runPatientSearch(value.trim()), 300);
  }

  private runPatientSearch(query: string): void {
    const version = ++this.patientSearchVersion;
    this.messagesService.searchPatients(query, this.message.practice_id)
      .then(results => {
        if (version !== this.patientSearchVersion) { return; }
        this.patientSearchLoading = false;
        this.patientResults       = results || [];
        this.showPatientDropdown  = this.patientResults.length > 0;
      })
      .catch(() => {
        if (version !== this.patientSearchVersion) { return; }
        this.patientSearchLoading = false;
      });
  }

  selectPatient(p: any): void {
    this.selectedPatient     = p;
    this.addToChartPatientId = p.id;
    this.patientQuery        = p.name || '';
    this.patientAutoDetected = false;
    this.showPatientDropdown = false;
    this.patientResults      = [];
  }

  clearPatient(): void {
    this.resetPatientSearch();
  }

  closePatientDropdownDelayed(): void {
    setTimeout(() => { this.showPatientDropdown = false; }, 150);
  }

  get patientInitial(): string {
    return this.selectedPatient?.name?.charAt(0)?.toUpperCase() || '?';
  }

  // complete_detail format: "First Last, Male / 35 Years - 0300-1234567"
  patientDemographics(p: any): string {
    if (!p?.complete_detail) { return ''; }
    const dashIdx = p.complete_detail.lastIndexOf(' - ');
    const head = dashIdx >= 0 ? p.complete_detail.substring(0, dashIdx) : p.complete_detail;
    const commaIdx = head.indexOf(', ');
    return commaIdx >= 0 ? head.substring(commaIdx + 2) : '';
  }

  patientPhone(p: any): string {
    if (!p?.complete_detail) { return ''; }
    const dashIdx = p.complete_detail.lastIndexOf(' - ');
    return dashIdx >= 0 ? p.complete_detail.substring(dashIdx + 3) : '';
  }

  patientInitialFor(p: any): string {
    return p?.name?.charAt(0)?.toUpperCase() || '?';
  }
}
